import { DownloadModal } from '@features/reports/components/downloadCentre/index'
import { CSV_EXPORT_TYPE } from '@features/reports/components/downloadCentre/utils'
import { Button, Icon, PageHeader, RangePicker } from '@lmc/ui-kit'
import { useRequestedHomeSwitch } from '@shared/hooks/useRequestedHomeSwitch'
import { useAbilityAccess } from 'components/AccessControl'
import dayjs from 'dayjs'
import { debounce, reduce } from 'lodash'
import qs from 'qs'
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { isBrowser, isTablet } from 'react-device-detect'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import Actions from '../../../actions'
import { defaultDateRanges } from '../../../common/constants'
import { gaEvent as trackGaEvent } from '../../../common/services/analytics'
import {
  PropertyFilters,
  PropertyFilterTags
} from '../../../components/filters'
import ListHeaderSearch from '../../../LEGACY/ListHeaderSearch'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import { commonPatterns } from '../../../utils/enhancedTestIds'
import { LogsTable } from '../components/dashboard'
import DownloadLogsCSVButton from '../components/dashboard/DownloadLogsCSVButton'

const FETCH_DEBOUNCE = 500
const isDesktop = isBrowser || isTablet
const MODAL_ID = 'addLog'
const SEARCH_BOX_PLACEHOLDER = 'Search by title or description'

const getQuery = () =>
  window.location.search && qs.parse(window.location.search.replace('?', ''))

type ActiveFilters = Record<string, string[] | undefined>

type LogsDashboardProps = {
  activeFilters?: ActiveFilters
  allFilters?: Record<string, { filters: unknown[] }> | null
  clearFilters: () => void
  clearSelectedFilter: (filterKey: string) => void
  exporting?: boolean
  fetchLogs: (params: {
    search: string
    timeLogged: Record<string, string>
  }) => void
  goToFirstPage: () => void
  loading?: boolean
  openModal: () => void
  preferredCarePlansNominative?: string
  preferredPeopleNominative?: string
  toggleActiveFilter: (params: {
    key: string
    value: string | string[]
  }) => void
}

type TimeLoggedState = {
  from?: dayjs.Dayjs
  to?: dayjs.Dayjs
}

function buildInitialTimeLogged(): TimeLoggedState {
  const raw = getQuery()
  const query = (raw && typeof raw === 'object' ? raw : {}) as Record<
    string,
    string | undefined
  >
  const dateTimeFormat = query.isPreciseDateTime ? undefined : 'DD-MM-YYYY'
  const startOfDay = query.isPreciseDateTime ? undefined : 'day'
  const endOfDay = query.isPreciseDateTime ? undefined : 'day'

  const fromParsed = query.from
    ? dateTimeFormat
      ? dayjs(query.from, dateTimeFormat)
      : dayjs(query.from)
    : null
  const toParsed = query.to
    ? dateTimeFormat
      ? dayjs(query.to, dateTimeFormat)
      : dayjs(query.to)
    : null

  return {
    from: fromParsed
      ? startOfDay
        ? fromParsed.startOf(startOfDay)
        : fromParsed
      : dayjs().subtract(1, 'month').startOf('day'),
    to: toParsed
      ? endOfDay
        ? toParsed.endOf(endOfDay)
        : toParsed
      : dayjs().endOf('day')
  }
}

function LogsDashboardScreen() {
  const dispatch = useDispatch<any>()

  useRequestedHomeSwitch()

  const activeFilters = useSelector(
    (state: { global: { filters: { logs?: ActiveFilters } } }) =>
      state.global.filters.logs
  )
  const allFilters = useSelector((state: unknown) =>
    Selectors.getLogFilters(state)
  )
  const exporting = useSelector(
    (state: { logs: { exporting?: boolean } }) => state.logs.exporting
  )
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: { preferredPeopleNominative?: string } } }) =>
      state.global.home.preferredPeopleNominative
  )
  const preferredCarePlansNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative?: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )
  const loading = useSelector(
    (state: { global: { loading?: boolean } }) => state.global.loading
  )

  const fetchLogs = useCallback(
    (params: { search: string; timeLogged: Record<string, string> }) => {
      dispatch(Operations.fetchLogs(params))
    },
    [dispatch]
  )

  const goToFirstPage = useCallback(() => {
    dispatch(Actions.setLogsPageNumber(1))
  }, [dispatch])

  const openModal = useCallback(() => {
    dispatch(Actions.openModal({ modalId: MODAL_ID }))
    dispatch(Actions.addForm({ formId: MODAL_ID }))
  }, [dispatch])

  const clearFilters = useCallback(() => {
    dispatch(Actions.resetFilter({ filterId: 'logs' }))
  }, [dispatch])

  const clearSelectedFilter = useCallback(
    (filterKey: string) => {
      dispatch(Actions.resetSelectedFilter({ filterId: 'logs', filterKey }))
    },
    [dispatch]
  )

  const toggleActiveFilter = useCallback(
    (params: { key: string; value: string | string[] }) => {
      dispatch(
        Actions.toggleActiveFilter({
          filterId: 'logs',
          key: params.key,
          value: params.value
        })
      )
    },
    [dispatch]
  )

  return (
    <LogsDashboard
      activeFilters={activeFilters}
      allFilters={allFilters}
      clearFilters={clearFilters}
      clearSelectedFilter={clearSelectedFilter}
      exporting={exporting}
      fetchLogs={fetchLogs}
      goToFirstPage={goToFirstPage}
      loading={loading}
      openModal={openModal}
      preferredCarePlansNominative={preferredCarePlansNominative}
      preferredPeopleNominative={preferredPeopleNominative}
      toggleActiveFilter={toggleActiveFilter}
    />
  )
}

export function LogsDashboard({
  activeFilters,
  allFilters,
  clearFilters,
  clearSelectedFilter,
  exporting,
  fetchLogs,
  goToFirstPage,
  loading,
  openModal,
  preferredCarePlansNominative,
  preferredPeopleNominative,
  toggleActiveFilter
}: LogsDashboardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [timeLogged, setTimeLogged] = useState<TimeLoggedState>(
    buildInitialTimeLogged
  )

  const canCreateLog = useAbilityAccess([{ i: 'create', this: 'Log' }])

  const timeLoggedRef = useRef(timeLogged)
  timeLoggedRef.current = timeLogged

  const searchRef = useRef(search)
  searchRef.current = search

  const fetchLogsFromRefs = useCallback(() => {
    const s = searchRef.current
    const tl = timeLoggedRef.current
    const withFormattedDates = reduce(
      tl,
      (result: Record<string, string>, value, key) => {
        if (value) result[key] = value.toISOString()
        return result
      },
      {} as Record<string, string>
    )
    fetchLogs({ search: s, timeLogged: withFormattedDates })
  }, [fetchLogs])

  const fireGaEvent = useCallback(
    ({ label, action }: { label?: string; action: string }) =>
      trackGaEvent({
        category: 'Logs Dashboard',
        action,
        label
      }),
    []
  )

  const debouncedFetchLogs = useMemo(
    () => debounce(fetchLogsFromRefs, FETCH_DEBOUNCE, {}),
    [fetchLogsFromRefs]
  )

  useEffect(
    () => () => {
      debouncedFetchLogs.cancel()
    },
    [debouncedFetchLogs]
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing dependency list; changing when this re-runs needs behavioural review
  useLayoutEffect(() => {
    const query = getQuery()
    if (query && typeof query === 'object' && Object.keys(query).length) {
      clearFilters()
      const { resident, item, category, items, loggedBy } = query as Record<
        string,
        string
      >

      if (resident) {
        toggleActiveFilter({ key: 'resident', value: resident })
      }
      if (item) {
        toggleActiveFilter({ key: 'item', value: item })
      }
      if (items) {
        toggleActiveFilter({ key: 'item', value: items })
      }
      if (category) {
        toggleActiveFilter({ key: 'category', value: category })
      }
      if (loggedBy) {
        toggleActiveFilter({ key: 'loggedBy', value: loggedBy })
      }
    }
    fetchLogsFromRefs()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only URL hydration + initial fetch
  }, [])

  const handleFilterChange = useCallback(
    ({ key, value }: { key: string; value: string }) => {
      goToFirstPage()
      if (key === 'doneBySu' && !activeFilters?.[key]?.includes(value)) {
        clearSelectedFilter('doneBySu')
      }
      toggleActiveFilter({ key, value })
      debouncedFetchLogs()
      fireGaEvent({
        action: 'Set Filter',
        label: key
      })
    },
    [
      activeFilters,
      clearSelectedFilter,
      debouncedFetchLogs,
      fireGaEvent,
      goToFirstPage,
      toggleActiveFilter
    ]
  )

  const handleOpenModal = useCallback(() => {
    window.scrollTo(0, 0)
    fireGaEvent({ action: 'Open Add Log modal' })
    openModal()
  }, [fireGaEvent, openModal])

  const handleSearchChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      searchRef.current = newValue
      setSearch(newValue)
      goToFirstPage()
      debouncedFetchLogs()
      fireGaEvent({
        action: 'Text Search'
      })
    },
    [debouncedFetchLogs, fireGaEvent, goToFirstPage]
  )

  const handleSearchClear = useCallback(() => {
    setSearch('')
    goToFirstPage()
    searchRef.current = ''
    fetchLogsFromRefs()
  }, [fetchLogsFromRefs, goToFirstPage])

  const handleTimeLoggedChange = useCallback(
    (range: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
      const [startDate, endDate] = range ?? [null, null]
      let next: TimeLoggedState = {}
      if (startDate) next = { from: dayjs(startDate).startOf('day') }
      if (endDate) next = { ...next, to: dayjs(endDate).endOf('day') }
      if (!startDate && !endDate) {
        next = {
          from: dayjs().startOf('day').subtract(6, 'month')
        }
      }

      setTimeLogged(next)
      goToFirstPage()
      timeLoggedRef.current = next
      fetchLogsFromRefs()
      fireGaEvent({ action: 'Date Search' })
    },
    [fetchLogsFromRefs, fireGaEvent, goToFirstPage]
  )

  const openDownloadModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const closeDownloadModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const renderHeaderButtons = () => {
    const csvDisabled = Boolean(exporting || loading)
    return (
      <ButtonsContainer>
        <DownloadLogsCSVButton
          openModal={openDownloadModal}
          disabled={csvDisabled}
          exporting={Boolean(exporting)}
          testId="logs-dashboard"
        />
        <AddLogButtonWrap>
          <Button
            onClick={() => handleOpenModal()}
            variant="primary"
            icon={<Icon name="PlusOutlined" />}
            disabledByPermission={!canCreateLog}
            data-cy="add-log"
            data-testid={commonPatterns.button('logs-dashboard', 'add')}
          >
            Add Log
          </Button>
        </AddLogButtonWrap>
      </ButtonsContainer>
    )
  }

  const searchInputStyle = useMemo(() => ({ fontSize: 16 }), [])

  const renderSearchInput = () => (
    <ListHeaderSearch
      focusInput={undefined}
      handleChange={handleSearchChange}
      handleClear={handleSearchClear}
      handleKeyup={undefined}
      inputStyle={searchInputStyle}
      multiline={undefined}
      placeholder={SEARCH_BOX_PLACEHOLDER}
      testId={undefined}
      value={search}
      data-testid={commonPatterns.input('logs-dashboard', 'search')}
    />
  )

  const renderDatePicker = () => (
    <DatePickerWrap
      data-testid={commonPatterns.input('logs-dashboard', 'date-range')}
    >
      <StyledRangePicker
        presets={defaultDateRanges as never}
        format="DD-MM-YYYY"
        defaultValue={[timeLogged?.from, timeLogged?.to]}
        disabledDate={(date) => date.isAfter(dayjs())}
        onChange={(dates) => {
          if (!dates) return null
          handleTimeLoggedChange(
            dates as [dayjs.Dayjs | null, dayjs.Dayjs | null]
          )
          return null
        }}
      />
    </DatePickerWrap>
  )

  const renderFilters = () => (
    <FiltersWrap>
      <PropertyFilters
        activeFilters={(activeFilters ?? {}) as { [key: string]: string[] }}
        allFilters={
          (allFilters ?? {}) as {
            [key: string]: {
              filters: Array<{
                value: string
                label?: string
              }>
            }
          }
        }
        expandedView={isDesktop}
        onChange={handleFilterChange}
        testId="logs-dashboard"
      />
    </FiltersWrap>
  )

  const renderFilterTags = () => (
    <FilterTagsOuter>
      <PropertyFilterTags
        style={{}}
        activeFilters={(activeFilters ?? {}) as { [key: string]: string[] }}
        allFilters={
          (allFilters ?? {}) as {
            [key: string]: {
              filters: Array<{
                value: string
                label?: string
              }>
            }
          }
        }
        onChange={handleFilterChange}
        testId="logs-dashboard"
      />
    </FilterTagsOuter>
  )

  const mappedFilters = {
    carers: activeFilters?.loggedBy,
    dateRange: Object.values(timeLogged),
    logCategories: activeFilters?.category,
    logCategoryItems: activeFilters?.item,
    residents: activeFilters?.resident
  }

  return (
    <PageWrapper data-testid="logs-dashboard">
      {/* Mobile layout */}
      {!isDesktop && renderHeaderButtons()}
      {!isDesktop && <SearchWrapper>{renderSearchInput()}</SearchWrapper>}
      {!isDesktop && renderDatePicker()}

      {/* Desktop layout */}
      {isDesktop && (
        <>
          <PageHeader title="Logs">
            <HeaderActionsRow>
              <HeaderSearchWrapper>{renderSearchInput()}</HeaderSearchWrapper>
              {renderHeaderButtons()}
            </HeaderActionsRow>
          </PageHeader>

          <ControlsCard>
            {renderDatePicker()}
            {renderFilters()}
          </ControlsCard>

          <DownloadModal
            isOpen={isModalOpen}
            onClose={closeDownloadModal}
            CSVExportType={CSV_EXPORT_TYPE.LOGS}
            preferredPeopleNominative={preferredPeopleNominative ?? ''}
            preferredCarePlansNominative={preferredCarePlansNominative ?? ''}
            formValues={{ ...mappedFilters }}
          />
        </>
      )}

      <div>{renderFilterTags()}</div>
      <LogsTable onChange={fetchLogsFromRefs} testId="logs-dashboard-table" />
    </PageWrapper>
  )
}

const AddLogButtonWrap = styled.div`
  margin-left: 6px;
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  position: relative;
  ${!isDesktop && 'margin-bottom: 24px;'}
`

const ControlsCard = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: white;
  border-radius: 15px;
  padding: 8px 22px;
  margin-top: 12px;
`

const DatePickerWrap = styled.div`
  padding: 5px;
  margin-top: -3px;
`

const StyledRangePicker = styled(RangePicker)`
  &.ant-picker {
    border-color: ${({ theme }) => theme?.color?.neutral?.color6 ?? '#BFBFBF'};
    border-radius: 9px;
  }
  &.ant-picker:hover,
  &.ant-picker-focused {
    border-color: ${({ theme }) => theme?.color?.hover?.primary ?? '#5346d6'};
  }
`

const FilterTagsOuter = styled.div`
  padding-bottom: 12px;
`

const FiltersWrap = styled.div`
  padding: 5px;
`

const HeaderActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HeaderSearchWrapper = styled.div`
  width: 320px;
  padding-left: 5px;
`

const PageWrapper = styled.div`
  margin: 24px 24px 50px;
`

const SearchWrapper = styled.div`
  max-width: 600px;
  padding-left: 5px;
`

export default LogsDashboardScreen
